/**
 * Cliente de simulación del Laboratorio EVSE (TG-97, ADR 0029).
 *
 * Endpoints (tag `noc`):
 * - `POST /api/v1/noc/sim/command`  → proxy de comandos OCPP al core (downlink).
 * - `POST /api/v1/noc/sim/inject`   → inyección en el VCP (uplink, allowlist CP-001).
 * - `POST /api/v1/noc/sim/connect`  → conexión efímera + BootNotification (prueba de alta).
 * - `GET  /api/v1/noc/sim/catalog`  → escenarios disponibles.
 * - `POST /api/v1/noc/sim/scenario` → lanza un escenario (progreso por SSE).
 * - `GET  /api/v1/noc/sim/scenario/:runId` → estado de una ejecución.
 */
import type {
  NocSimCatalogResponse,
  NocSimCommandRequest,
  NocSimCommandResult,
  NocSimConnectRequest,
  NocSimConfigResponse,
  NocSimConnectResult,
  NocSimInjectRequest,
  NocSimInjectResult,
  NocSimScenarioRun,
  NocSimScenarioRunSummary,
} from '@goelectric/shared-types';
import { apiRequest } from '@/lib/http/apiClient';

export const simApi = {
  catalog(): Promise<NocSimCatalogResponse> {
    return apiRequest<NocSimCatalogResponse>('/api/v1/noc/sim/catalog');
  },

  /** Allowlists del backend (la UI no las hardcodea). */
  config(): Promise<NocSimConfigResponse> {
    return apiRequest<NocSimConfigResponse>('/api/v1/noc/sim/config');
  },

  /** Downlink: traduce el comando de dominio y lo envía por la Message API del core. */
  command(body: NocSimCommandRequest): Promise<NocSimCommandResult> {
    return apiRequest<NocSimCommandResult>('/api/v1/noc/sim/command', {
      method: 'POST',
      body,
      retryOnUnauthorized: false,
    });
  },

  /** Uplink: fuerza un mensaje OCPP en el VCP (solo estaciones con VCP habilitado). */
  inject(body: NocSimInjectRequest): Promise<NocSimInjectResult> {
    return apiRequest<NocSimInjectResult>('/api/v1/noc/sim/inject', {
      method: 'POST',
      body,
      retryOnUnauthorized: false,
    });
  },

  /** Conexión efímera del cargador contra el core: envía BootNotification y devuelve el veredicto. */
  connect(body: NocSimConnectRequest): Promise<NocSimConnectResult> {
    return apiRequest<NocSimConnectResult>('/api/v1/noc/sim/connect', {
      method: 'POST',
      body,
      retryOnUnauthorized: false,
    });
  },

  startScenario(scenarioId: string): Promise<NocSimScenarioRunSummary> {
    return apiRequest<NocSimScenarioRunSummary>('/api/v1/noc/sim/scenario', {
      method: 'POST',
      body: { scenarioId },
      retryOnUnauthorized: false,
    });
  },

  scenarioRun(runId: string): Promise<NocSimScenarioRun | null> {
    return apiRequest<NocSimScenarioRun | null>(
      `/api/v1/noc/sim/scenario/${encodeURIComponent(runId)}`,
    );
  },
};
